import { unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, SelectField, TextField } from '@mrigl/ui';
import { useEffect, useState, type SubmitEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  SearchFilter,
  SortHeader,
  useGridState,
  usePagedData,
  useRowLink,
  type ListQuery,
} from '../../grid.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, toMajor, toMinor, useStore, type StoreDetails } from '../catalog/shared.js';
import {
  checkoutError,
  fromBasisPoints,
  toBasisPoints,
  tunisDate,
  tunisDayStart,
} from './shared.js';

type Discount = Schemas['Discount'];
type DiscountInput = Schemas['DiscountInput'];
type Kind = Discount['kind'];
type Status = Discount['status'];

const KINDS: readonly Kind[] = ['percentage', 'fixed_amount', 'free_shipping'];
const CODE = /^[A-Za-z0-9_-]{3,40}$/;

function value(discount: Discount, t: I18n['t'], formatPrice: I18n['formatPrice']): string {
  if (discount.kind === 'percentage') {
    return t('admin.discount.value.percentage', {
      percent: fromBasisPoints(discount.percentBp ?? 0),
    });
  }
  if (discount.kind === 'fixed_amount') return formatPrice(discount.amount?.minor ?? '0');
  return t('admin.discount.kind.free_shipping');
}

function validity(discount: Discount, t: I18n['t']): string {
  const start = discount.startsAt === null ? null : tunisDate(discount.startsAt);
  const end = discount.endsAt === null ? null : tunisDate(discount.endsAt, true);
  if (start !== null && end !== null) return t('admin.discount.between', { start, end });
  if (start !== null) return t('admin.discount.from', { date: start });
  if (end !== null) return t('admin.discount.until', { date: end });
  return t('admin.discount.always');
}

type DiscountsQuery = ListQuery<'/admin-api/v1/stores/{storeId}/discounts'>;
const STATES: readonly Discount['state'][] = [
  'active',
  'scheduled',
  'expired',
  'exhausted',
  'disabled',
];

/** Discount codes of the store: code search, situation filter, sortable columns, pages. */
export function DiscountsPage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatPrice } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const grid = useGridState({
    sort: '-createdAt',
    sortable: ['code', 'timesUsed', 'endsAt', 'createdAt'],
    filters: { q: '', state: '' },
  });
  const rowLink = useRowLink();
  const { filters } = grid;
  const key = JSON.stringify([storeId, grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error } = usePagedData<Discount>(
    key,
    () =>
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/discounts', {
          params: {
            path: { storeId },
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as NonNullable<DiscountsQuery['sort']>,
              ...(filters.q === '' ? {} : { q: filters.q }),
              ...(filters.state === '' ? {} : { state: filters.state as Discount['state'] }),
            },
          },
        }),
      ),
    grid.setPage,
  );

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null) return <Loading />;
  const header = (field: string, label: string, firstDescending = false, numeric = false) => (
    <SortHeader
      field={field}
      label={label}
      sort={grid.sort}
      onSort={grid.setSort}
      firstDescending={firstDescending}
      numeric={numeric}
    />
  );
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <div className="admin-title-row">
        <h1 className="mrigl-heading">{t('admin.discount.title')}</h1>
        <Link
          className="mrigl-button mrigl-button--primary admin-cta"
          to={`/stores/${storeId}/discounts/new`}
        >
          {t('admin.discount.new')}
        </Link>
      </div>
      <GridToolbar
        activeFilters={grid.activeFilters}
        onReset={grid.reset}
        search={
          <SearchFilter
            label={t('admin.discount.search')}
            value={filters.q}
            onChange={(value) => {
              grid.setFilter('q', value);
            }}
          />
        }
      >
        <SelectField
          label={t('admin.discount.state')}
          value={filters.state}
          onChange={(event) => {
            grid.setFilter('state', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.discount.filter.allStates') },
            ...STATES.map((value) => ({ value, label: t(`admin.discount.state.${value}`) })),
          ]}
        />
      </GridToolbar>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.discount.empty')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  {header('code', t('admin.discount.column.code'))}
                  <th scope="col">{t('admin.discount.column.value')}</th>
                  {header('timesUsed', t('admin.discount.column.used'), true, true)}
                  {header('endsAt', t('admin.discount.column.validity'))}
                  <th scope="col">{t('admin.discount.state')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((discount) => (
                  <tr
                    key={discount.discountId}
                    {...rowLink(`/stores/${storeId}/discounts/${discount.discountId}`)}
                  >
                    <td className="mrigl-ltr">
                      <Link
                        className="admin-cell-link admin-code"
                        to={`/stores/${storeId}/discounts/${discount.discountId}`}
                      >
                        {discount.code}
                      </Link>
                    </td>
                    <td>{value(discount, t, formatPrice)}</td>
                    <td className="mrigl-ltr admin-num">
                      {discount.usageLimit === null
                        ? discount.timesUsed
                        : `${String(discount.timesUsed)} / ${String(discount.usageLimit)}`}
                    </td>
                    <td>{validity(discount, t)}</td>
                    <td>
                      <span className={`admin-status admin-status--discount-${discount.state}`}>
                        {t(`admin.discount.state.${discount.state}`)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={grid.page}
            pageSize={grid.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
    </div>
  );
}

interface Draft {
  readonly code: string;
  readonly kind: Kind;
  readonly percent: string;
  readonly amount: string;
  readonly minSubtotal: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly usageLimit: string;
  readonly perCustomerLimit: string;
  readonly status: Status;
}

function fromDiscount(discount: Discount | null): Draft {
  if (discount === null) {
    return {
      code: '',
      kind: 'percentage',
      percent: '',
      amount: '',
      minSubtotal: '',
      startsOn: '',
      endsOn: '',
      usageLimit: '',
      perCustomerLimit: '',
      status: 'active',
    };
  }
  return {
    code: discount.code,
    kind: discount.kind,
    percent: discount.percentBp === null ? '' : fromBasisPoints(discount.percentBp),
    amount: discount.amount === null ? '' : toMajor(discount.amount.minor),
    minSubtotal: discount.minSubtotal === null ? '' : toMajor(discount.minSubtotal.minor),
    startsOn: discount.startsAt === null ? '' : tunisDate(discount.startsAt),
    endsOn: discount.endsAt === null ? '' : tunisDate(discount.endsAt, true),
    usageLimit: discount.usageLimit === null ? '' : String(discount.usageLimit),
    perCustomerLimit: discount.perCustomerLimit === null ? '' : String(discount.perCustomerLimit),
    status: discount.status,
  };
}

/** Create or edit a discount code. */
export function DiscountEditorPage() {
  const { storeId = '', discountId } = useParams();
  const { t } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [discount, setDiscount] = useState<Discount | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (discountId === undefined) return;
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/discounts/{discountId}', {
        params: { path: { storeId, discountId } },
      }),
    )
      .then(setDiscount)
      .catch((failure: unknown) => {
        setLoadError(errorMessage(t, failure));
      });
  }, [storeId, discountId, t]);

  const error = storeError ?? loadError;
  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (store === null || (discountId !== undefined && discount === null)) return <Loading />;
  return (
    <DiscountEditor
      key={discount?.discountId ?? 'new'}
      store={store}
      discount={discount}
      onSaved={setDiscount}
    />
  );
}

function DiscountEditor({
  store,
  discount,
  onSaved,
}: {
  readonly store: StoreDetails;
  readonly discount: Discount | null;
  readonly onSaved: (discount: Discount) => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [draft, setDraft] = useState<Draft>(() => fromDiscount(discount));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const update = (change: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...change }));
  };

  function build(): DiscountInput | null {
    const found: Record<string, string> = {};
    if (!CODE.test(draft.code.trim())) found['code'] = t('admin.discount.codeHint');
    const percentBp = draft.kind === 'percentage' ? toBasisPoints(draft.percent) : null;
    if (draft.kind === 'percentage' && (percentBp === null || percentBp === 0)) {
      found['percent'] = t('admin.discount.invalidPercent');
    }
    const amount = draft.kind === 'fixed_amount' ? toMinor(draft.amount) : null;
    if (draft.kind === 'fixed_amount' && (amount === null || amount === '0')) {
      found['amount'] = t('admin.checkout.invalidAmount');
    }
    const minSubtotal = draft.minSubtotal.trim() === '' ? null : toMinor(draft.minSubtotal);
    if (draft.minSubtotal.trim() !== '' && minSubtotal === null) {
      found['minSubtotal'] = t('admin.checkout.invalidAmount');
    }
    const count = (text: string, field: string) => {
      if (text.trim() === '') return null;
      if (!/^[1-9][0-9]{0,6}$/.test(text.trim())) {
        found[field] = t('admin.discount.invalidNumber');
        return null;
      }
      return Number(text.trim());
    };
    const usageLimit = count(draft.usageLimit, 'usageLimit');
    const perCustomerLimit = count(draft.perCustomerLimit, 'perCustomerLimit');
    const startsAt = draft.startsOn === '' ? null : tunisDayStart(draft.startsOn);
    const endsAt = draft.endsOn === '' ? null : tunisDayStart(draft.endsOn, true);
    if (startsAt !== null && endsAt !== null && endsAt <= startsAt) {
      found['endsOn'] = t('admin.discount.invalidDates');
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return null;
    return {
      code: draft.code.trim(),
      kind: draft.kind,
      percentBp,
      amount: amount === null ? null : { minor: amount, currency: 'TND' },
      minSubtotal: minSubtotal === null ? null : { minor: minSubtotal, currency: 'TND' },
      startsAt,
      endsAt,
      usageLimit,
      perCustomerLimit,
      status: draft.status,
    };
  }

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    setError(null);
    const input = build();
    if (input === null) {
      setError(t('admin.discount.error.validation'));
      return;
    }
    setBusy(true);
    try {
      if (discount === null) {
        const created = await unwrap(
          api.POST('/admin-api/v1/stores/{storeId}/discounts', {
            params: { path: { storeId: store.storeId } },
            body: input,
          }),
        );
        void navigate(`/stores/${store.storeId}/discounts/${created.discountId}`, {
          replace: true,
          state: { notice: t('admin.discount.saved') },
        });
      } else {
        const saved = await unwrap(
          api.PUT('/admin-api/v1/stores/{storeId}/discounts/{discountId}', {
            params: { path: { storeId: store.storeId, discountId: discount.discountId } },
            body: { ...input, version: discount.version },
          }),
        );
        onSaved(saved);
        setDraft(fromDiscount(saved));
        setNotice(t('admin.discount.saved'));
      }
    } catch (failure) {
      setError(checkoutError(t, failure, 'discount'));
    } finally {
      setBusy(false);
    }
  }

  const heading = discount === null ? t('admin.discount.new') : discount.code;
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${store.storeId}/discounts`}>{t('admin.discount.title')}</Link>
      </p>
      <h1 className="mrigl-heading mrigl-ltr">{heading}</h1>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form
        className="mrigl-card mrigl-stack"
        aria-label={discount === null ? t('admin.discount.new') : t('admin.discount.edit')}
        onSubmit={(event) => void save(event)}
      >
        <TextField
          label={t('admin.discount.code')}
          hint={t('admin.discount.codeHint')}
          dir="ltr"
          maxLength={40}
          required
          value={draft.code}
          error={errors['code']}
          onChange={(event) => {
            update({ code: event.target.value.toUpperCase() });
          }}
        />
        <SelectField
          label={t('admin.discount.kind')}
          value={draft.kind}
          onChange={(event) => {
            update({ kind: event.target.value as Kind });
          }}
          options={KINDS.map((kind) => ({ value: kind, label: t(`admin.discount.kind.${kind}`) }))}
        />
        {draft.kind === 'percentage' ? (
          <TextField
            label={t('admin.discount.percent')}
            inputMode="decimal"
            required
            value={draft.percent}
            error={errors['percent']}
            onChange={(event) => {
              update({ percent: event.target.value });
            }}
          />
        ) : null}
        {draft.kind === 'fixed_amount' ? (
          <TextField
            label={t('admin.discount.amount')}
            inputMode="decimal"
            required
            value={draft.amount}
            error={errors['amount']}
            onChange={(event) => {
              update({ amount: event.target.value });
            }}
          />
        ) : null}
        <TextField
          label={t('admin.discount.minSubtotal')}
          inputMode="decimal"
          value={draft.minSubtotal}
          error={errors['minSubtotal']}
          onChange={(event) => {
            update({ minSubtotal: event.target.value });
          }}
        />
        <TextField
          type="date"
          label={t('admin.discount.startsOn')}
          hint={t('admin.discount.datesHint')}
          value={draft.startsOn}
          onChange={(event) => {
            update({ startsOn: event.target.value });
          }}
        />
        <TextField
          type="date"
          label={t('admin.discount.endsOn')}
          value={draft.endsOn}
          error={errors['endsOn']}
          onChange={(event) => {
            update({ endsOn: event.target.value });
          }}
        />
        <TextField
          label={t('admin.discount.usageLimit')}
          inputMode="numeric"
          value={draft.usageLimit}
          error={errors['usageLimit']}
          onChange={(event) => {
            update({ usageLimit: event.target.value });
          }}
        />
        <TextField
          label={t('admin.discount.perCustomerLimit')}
          hint={t('admin.discount.perCustomerHint')}
          inputMode="numeric"
          value={draft.perCustomerLimit}
          error={errors['perCustomerLimit']}
          onChange={(event) => {
            update({ perCustomerLimit: event.target.value });
          }}
        />
        <SelectField
          label={t('admin.discount.status')}
          value={draft.status}
          onChange={(event) => {
            update({ status: event.target.value as Status });
          }}
          options={[
            { value: 'active', label: t('admin.discount.status.active') },
            { value: 'disabled', label: t('admin.discount.status.disabled') },
          ]}
        />
        <Button type="submit" busy={busy}>
          {t('admin.save')}
        </Button>
      </form>
    </div>
  );
}
